import React, { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  Box,
  SkeletonGroup,
  SkeletonRectangle,
  SkeletonLine,
} from '@wix/design-system';
import { TemplateGalleryCard } from '@common/types';
import { useIntersect } from '@wix/experts-common';
import { templatePath } from '@components/Hooks/useRouter';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import TemplateCardImage from './TemplateCardImage';
import CardInfo from './CardInfo';

import s from './TemplateCard.module.scss';
import { TEMPLATES_GALLERY_ENTRY } from '@utils/constants';

interface Props {
  template: TemplateGalleryCard;
  forceRender?: boolean;
  loader?: boolean;
  index: number;
}

const TemplateCard: FC<Props> = ({
  template,
  forceRender,
  loader = false,
  index,
}) => {
  const { t } = useTranslation();

  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [ready, setReady] = useState(false || forceRender);
  const [ref, entryElment] = useIntersect({}) as any;
  const { logBiClick } = useTemplatesBi({
    is_mobile: false,
  });

  useEffect(() => {
    if (entryElment.intersectionRatio === 1 && !ready && !loader) {
      setTimeout(() => {
        setReady(true);
      }, 200);
    }
  }, [ready, entryElment]);

  function buildTemplatePageLink() {
    return `${templatePath}/${template.id}?entry=${TEMPLATES_GALLERY_ENTRY}`;
  }

  function renderTemplateCard() {
    return (
      <Box
        className={s.template}
        direction="vertical"
        minWidth="232px"
        minHeight="322px"
        overflow="hidden"
        borderRadius="4px"
        verticalAlign="middle"
        dataHook="desktop-card"
      >
        <TemplateCardImage
          imageSource={template.media?.longThumbnail?.url}
          description={template.media?.longThumbnail?.description ?? ''}
          index={index}
          animatedPreview={template.media?.animatedPreview}
          hovered={hover}
        />
        <div onMouseEnter={() => setHover(true)}>
          <Box paddingTop="12px" display="block">
            <CardInfo
              template={template}
              isMobileView={false}
              hovered={hover}
            />
          </Box>
        </div>
      </Box>
    );
  }

  function renderLoadingCard() {
    return (
      <SkeletonGroup skin="light">
        <Box
          className={s.template}
          direction="vertical"
          minWidth="218px"
          minHeight="322px"
          padding="12px"
          overflow="hidden"
          borderRadius="4px"
        >
          <div ref={ref}></div>
          <div className={s.skeletonRectStyle}>
            <SkeletonRectangle />
          </div>
          <Box direction="horizontal" width="100%" align="space-between">
            <SkeletonLine width="132px" marginTop="12px" />

            <div className={s.skeletonLineStyle}>
              <SkeletonLine width="32px" marginTop="12px" />
            </div>
          </Box>
          <SkeletonLine width="132px" marginTop="4px" />
          <SkeletonLine width="46px" marginTop="36px" />
        </Box>
      </SkeletonGroup>
    );
  }

  return (
    <div
      aria-labelledby={`template-title-${template.title}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        template.media?.animatedPreview && setFocused(false);
      }}
    >
      <a
        data-hook={`template-card-container-${template.id}`}
        href={buildTemplatePageLink()}
        aria-label={t('marketplace.templates-page.view-template-cta')}
        className={s.cardContainer}
        onClick={() => {
          logBiClick({
            click_type: 'View Template',
            click_data: { template_id: template.id },
          });
        }}
      >
        {ready ? renderTemplateCard() : renderLoadingCard()}
      </a>
    </div>
  );
};

export default TemplateCard;
